import { EventSourcePolyfill } from "event-source-polyfill";
import { ApiClient, Page } from "./ApiClient";

export type LeadListType = "new" | "my" | "fav" | "team";

export interface ChatCustomer {
  id: number;
  name: string;
  leadNo?: string;
  star?: number;
  intentionLevel?: string;
  status?: string;
  score?: number;
  fullMobile?: string;
  applyCity?: string;
  applyAmount?: number;
}

export interface ChatTemplate {
  id: number;
  category: string;
  title: string;
  content: string;
  sortOrder: number;
}

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
  ts?: string;
}

const api = ApiClient.getDefault();

export interface ListLeadsParams {
  type: LeadListType;
  page?: number;
  size?: number;
  keyword?: string;
}

export const AiCoachChatApi = {
  /**
   * 走通用 /leads 接口取客户列表，支持四种类型 + 分页 + 模糊搜索。
   * 返回 Page，调用方自己 map 成 ChatCustomer。
   */
  listLeads: (params: ListLeadsParams) =>
    api.request<Page<any>>("/leads", "GET", {
      type: params.type,
      page: params.page ?? 1,
      size: params.size ?? 20,
      ...(params.keyword ? { name: params.keyword } : {}),
    }),

  /** 兼容保留：仅作过渡，新代码请用 listLeads */
  listCustomers: (keyword?: string) =>
    api.request<ChatCustomer[]>("/ai-coach/chat/customers", "GET", keyword ? { keyword } : undefined),
  listTemplates: (leadId?: number) =>
    api.request<ChatTemplate[]>("/ai-coach/chat/templates", "GET", leadId ? { leadId } : undefined),
  history: (leadId: number) =>
    api.request<ChatMessage[]>(`/ai-coach/chat/history/${leadId}`, "GET"),
  userPrompt: (leadId: number) =>
    api.request<string>(`/ai-coach/chat/user-prompt/${leadId}`, "GET"),
  clearHistory: (leadId: number) =>
    api.request<void>(`/ai-coach/chat/history/${leadId}`, "DELETE"),
};

const TOKEN_KEY = "X-Auth-Token";

/**
 * 用 EventSourcePolyfill 发起 SSE 流。
 * 浏览器原生 EventSource 不支持自定义 header；项目用 X-Auth-Token，必须用 polyfill。
 */
export function streamChat(
  leadId: number,
  msg: string,
  handlers: {
    onToken: (delta: string) => void;
    onDone: (info: { tokens: number }) => void;
    onError: (err: string) => void;
  }
): () => void {
  const token = localStorage.getItem(TOKEN_KEY) ?? "";
  const url = `/api/mis/ai-coach/chat/stream/${leadId}?msg=${encodeURIComponent(msg)}`;
  const es = new EventSourcePolyfill(url, {
    headers: token ? { "X-Auth-Token": token } : {},
    heartbeatTimeout: 120000,
  });

  es.addEventListener("token", (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);
      if (data.delta) handlers.onToken(data.delta);
    } catch { /* ignore */ }
  });
  es.addEventListener("done", (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);
      handlers.onDone(data);
    } catch {
      handlers.onDone({ tokens: 0 });
    }
    es.close();
  });
  es.addEventListener("error", (e: Event) => {
    const dataStr = (e as MessageEvent)?.data;
    if (dataStr) {
      try {
        const data = JSON.parse(dataStr);
        handlers.onError(data.msg ?? "未知错误");
      } catch {
        handlers.onError("连接异常");
      }
    } else {
      handlers.onError("连接断开");
    }
    es.close();
  });

  return () => es.close();
}
